import { DoorInfo, FillingElement, FrameElement } from "../../models/DoorInfo";
import { IObj2d, maxPos } from "../../models/IObj";
import { OverlapInfo } from "../../models/OverlapInfo";
import { updateElement } from "../../util/arraySlice";
import clamp from "../../util/clamp";
import range from "../../util/range";
import { InteriorService } from "./InteriorService";
import { Cabinet, CabinetPosition, ICabinet, IDoor, Door, IVec3d, Vec2d, CabinetClient, IExtraItem, ExtraItem, ErrorFlag, } from "../WebApiClient";
import { GableInfo, GableSideInfo } from "../../models/GableInfo";
import { GableType } from "../../models/GableType";
import { GableSide } from "../../models/GableSide";
import { hasDimensions } from "../CabinetValidator";
import { ICabinetWithDimensions } from "../../models/IcabinetWithDimensions";
import { replaceInArray } from "../../util/replaceInArray";
import {
    IChainInfo,
    IDoorBar,
    IDoorProduct,
    IProductLine,
    IMaterial,
    IVec2d
} from "../../models/IChainInfo";
import { CabinetError } from "../../models/CabinetError";
import { addFlag } from "../../util/flags";


export enum ResetTopic {
    Dimensions = "Dimensions",
    Corpus = "Corpus",
    Doors = "Doors",
    Interior = "Interior",
}

export interface ChangeResult {
    success: boolean;
}
function success(): ChangeResult {
    return { success: true };
}
function failure(): ChangeResult {
    return { success: false };
}

export type ResetConfirmer = (topics: ResetTopic[]) => Promise<boolean>;

export class CabinetService {
    private readonly doorOverlapWidth = 34;

    public readonly interior: InteriorService;
    private _cabinet: ICabinet;

    constructor(
        public readonly chainInfo: IChainInfo,
        private readonly resetConfirmer: ResetConfirmer,
        cabinet: ICabinet,
        private readonly setCabinet: (c: ICabinet) => void
    ) {
        this._cabinet = cabinet;
        this.interior = new InteriorService(this,
            cabinet,
            (cabinetChanges) => this.change(cabinetChanges));
    }

    public get cabinet(): Readonly<ICabinet> { return this._cabinet; }

    static createDefaultCabinet(chainInfo: IChainInfo): Cabinet {

        return new Cabinet({
            height: null,
            width: null,
            depth: null,
            position: null,
            roof: false, //Roof is disabled completely for now
            bottom: false, //disabled completely for now
            doorDampening: null,
            doorProductId: null,
            doors: [],
            extraItems: [],
            interiorItems: [],
            leftGableMaterialId: null,
            rightGableMaterialId: null,
            numDoorOverlaps: null,
            productLineId: null,
            sidePanel: null,
            interiorMaterialId: chainInfo.interiorMaterials.length === 0
                ? null
                : chainInfo.interiorMaterials[0].id,
            userSolutionId: null,
            name: "",
            orderNumber: null,
        });
    }

    copyCabinet(cabinet: ICabinet): Cabinet {
        return new Cabinet(cabinet);
    }

    async loadSavedCabinet(userSolutionId: string, client: CabinetClient) {
        const response = await client.getCabinet(userSolutionId);
        this.change(response.cabinet);
    }


    private change(changes: Partial<ICabinet>) {
        console.debug("Cabinet change made: ", changes);
        const newDoors = this.updateDoorDimensions(changes);
        const errorFlags = this.getErrorFlags(changes);
        const newCabinet = {
            ...this.cabinet,
            ...changes,
            doors: newDoors,
        };
        this._cabinet = newCabinet;
        this.setCabinet(newCabinet);
    }
    private changeDoor(doorIndex: number, changes: Partial<IDoor>) {
        const newDoors = updateElement(
            this.cabinet.doors as IDoor[],
            doorIndex,
            oldDoor => ({ ...oldDoor, ...changes })
        ).map(iDoor => new Door(iDoor));
        this.change({ doors: newDoors });
    }

    private exceedsBounds(n: number | null, min: number, max: number): boolean {
        if (n === null)
            return false;
        if (n < min)
            return true;
        if (n > max)
            return true;
        return false;
    }

    async initDimensions(): Promise<ChangeResult & { isChanged: boolean }> {
        const productLine = this.getProductLine();
        if (!productLine)
            return { success: false, isChanged: false };

        let changes: Partial<ICabinet> = {};
        let isChanged = false;
        if (this.cabinet.width === null) {
            changes.width = productLine.defaultSize.x;
            isChanged = true;
        }
        if (this.cabinet.height === null) {
            changes.height = productLine.defaultSize.y;
            isChanged = true;
        }
        if (this.cabinet.depth === null) {
            changes.depth = productLine.gableProducts[0].size.z;
            isChanged = true;
        }

        if (isChanged) {
            this.change(changes);
        }
        return { success: true, isChanged };
    }

    async initDoors(): Promise<ChangeResult & { isChanged: boolean }> {
        const doorProduct = this.getDoorProduct();
        let changes: Partial<ICabinet> = {};
        if (!doorProduct) {

            changes.doorDampening = false;
            changes.doors = [];
            changes.numDoorOverlaps = null;
            this.change(changes);

            return { success: true, isChanged: true };
        }


        let doors = this.cabinet.doors.slice();
        let isChanged = false;

        const numDoorChoices = this.getNumDoorChoices(changes);

        //add doors
        if (doors.length < numDoorChoices.min) {
            const numDoors = this.getOptimalNumberOfDoors();
            const newDoor = this.getNewDoor();
            if (!newDoor)
                return { success: false, isChanged: false };
            while (doors.length < numDoors) {
                doors.push(new Door(newDoor));
            }
            isChanged = true;
            changes.doors = doors;
        }

        //remove extra doors
        if (doors.length > numDoorChoices.max) {
            doors = doors.slice(0, numDoorChoices.max);
            changes.doors = doors;
            isChanged = true;
        }

        if (this.cabinet.doorDampening === null) {
            if (!this.supportsDoorDamping(doorProduct)) {
                changes.doorDampening = false;
                isChanged = true;
            }
        }

        //initOverlaps
        const numOverlapChoices = this.getOverlapInfos(changes)
        if (!numOverlapChoices.find(oi => oi.numOverlaps === this.cabinet.numDoorOverlaps)) {
            changes.numDoorOverlaps = numOverlapChoices[0].numOverlaps;
            isChanged = true;
        }

        //init gables and adapters, since they are on the same page as doors
        if (this.cabinet.leftGableMaterialId === null) {
            changes.leftGableMaterialId = this.getGableMaterials(GableSide.Left)[0]?.id ?? null;
            isChanged = true;
        }

        if (this.cabinet.rightGableMaterialId === null) {
            changes.rightGableMaterialId = this.getGableMaterials(GableSide.Right)[0]?.id ?? null;
            isChanged = true;
        }

        if (isChanged) {
            changes.doors = doors.map(iDoor => new Door(iDoor));
            this.change(changes);
        }
        return {
            success: true,
            isChanged: isChanged
        };
    }

    supportsDoorDamping(doorProduct: IDoorProduct | undefined): boolean {
        return !!(
            doorProduct?.damperLeft
            || doorProduct?.damperRight
            || doorProduct?.damperMiddle
        );

    }

    getGableWidthDeduction(side: GableSide, changes: Partial<ICabinet> = {}): {size: number, affectsInterior: boolean, affectsDoors: boolean} {
        const cabinet = {
            ...this.cabinet,
            ...changes
        };
        const materialId = side === GableSide.Left
            ? cabinet.leftGableMaterialId
            : cabinet.rightGableMaterialId;
        if (!this.hasWall(cabinet, side)) {
            const pl = this.getProductLine(changes);
            const size = pl?.gableProducts.filter(gp => gp.materials.some(mat => mat.id === materialId))[0]?.size.x ?? 0;
            return {
                size,
                affectsDoors: true,
                affectsInterior: true,
            };

        }
        else if (cabinet.sidePanel) {
            const pl = this.getProductLine(changes);
            const size = pl?.adapterProducts[0]?.size.x ?? 0;
            return {
                size,
                affectsInterior: false,
                affectsDoors: true,
            }
        }
        else {
            return {
                size: 0,
                affectsDoors: true,
                affectsInterior: false
            };
        }
    }

    getDampingStatus(doorIndex: number): "notAvailableOnDoorType" | "tooNarrow" | "activated" | "deactivated" {
        const doorProduct = this.getDoorProduct();
        if (!doorProduct)
            return "notAvailableOnDoorType";
        if (!this.canUseDampening())
            return "notAvailableOnDoorType";
        const doorInfos = this.getDoorInfos();
        if (!doorInfos)
            return "notAvailableOnDoorType";
        const damperProduct = (doorIndex === 0) ? doorProduct.damperLeft
            : (doorIndex === doorInfos.length - 1) ? doorProduct.damperRight
                : doorProduct.damperMiddle;
        if (damperProduct === null)
            return "notAvailableOnDoorType";
        if (doorInfos[doorIndex].size.x < damperProduct.minWidth) {
            return "tooNarrow";
        }
        return this.cabinet.doorDampening === true ? "activated" : "deactivated";
    }

    addExtraProduct(extraProductId: number, count: number, materialId: number | null) {
        const oldExtraItems = this.cabinet.extraItems as IExtraItem[];
        let newArray = replaceInArray(oldExtraItems,
            ei => ei.productId === extraProductId && ei.interiorMaterialId === materialId,
            ei => ({ ...ei, count: ei.count + count })
        );

        if (!newArray) {
            //No element was replaced - there were no extraItems with the correct productId
            newArray = oldExtraItems.concat({ count, productId: extraProductId, interiorMaterialId: materialId });
        }
        const changes: Partial<ICabinet> = {
            extraItems: newArray.map(ei => new ExtraItem(ei))
        };
        this.change(changes);
    }

    setExtraItemCount(extraProductId: number, newCount: number, materialId: number | null) {
        const oldExtraItems = this.cabinet.extraItems as IExtraItem[];
        let newArray = replaceInArray(oldExtraItems,
            ei => (ei.productId === extraProductId && ei.interiorMaterialId === materialId),
            ei => ({ ...ei, count: newCount })
        );
        if (!newArray) {
            //No element was replaced - there were no extraItems with the correct productId
            newArray = oldExtraItems.concat({
                count: newCount,
                productId: extraProductId,
                interiorMaterialId: materialId,
            });
        }
        const changes: Partial<ICabinet> = {
            extraItems: newArray.map(ei => new ExtraItem(ei))
        };
        this.change(changes);

    }

    removeExtraItem(extraProductId: number) {
        const oldExtraItems = this.cabinet.extraItems as IExtraItem[];
        const newExtraItems = oldExtraItems.filter(ei => ei.productId !== extraProductId);

        const changes: Partial<ICabinet> = {
            extraItems: newExtraItems.map(ei => new ExtraItem(ei))
        };
        this.change(changes);
    }


    private withNumDoors(numDoors: number, existingDoors: IDoor[]): Door[] {
        if (numDoors > existingDoors.length) {
            let door = existingDoors.length > 0 ? existingDoors[existingDoors.length - 1] : this.getNewDoor();
            if (!door)
                throw new Error("Could not make door");
            let newDoors = range(existingDoors.length, numDoors).map(_ => new Door(door));
            return existingDoors
                .map(d => new Door(d))
                .concat(...newDoors);
        }
        return existingDoors.slice(0, numDoors)
            .map(d => new Door(d));
    }

    private getOptimalNumberOfDoors(): number {
        //TODO calculate
        let nrOfDoorChoices = this.getNumDoorChoices();
        return nrOfDoorChoices.min;
    }

    private getNewDoor(changes: Partial<ICabinet> = {}): IDoor | undefined {
        if (this.cabinet.doors.length > 0) {
            const lastDoor = this.cabinet.doors[this.cabinet.doors.length - 1];
            return {
                bandMaterialId: lastDoor.bandMaterialId,
                barId: lastDoor.barId,
                materialId: lastDoor.materialId,
                size: new Vec2d(lastDoor.size)
            };
        }

        const doorProduct = this.getDoorProduct(changes);
        if (!doorProduct)
            return;
        return {
            bandMaterialId: doorProduct.bandMaterials[0]?.id ?? null,
            barId: doorProduct.bars[0]?.id ?? null,
            materialId: doorProduct.doorSizes[0]?.material?.id ?? null,
            size: new Vec2d({x:0, y:0})
        }
    }

    getNumDoorChoices(changes: Partial<ICabinet> = {}): { min: number, max: number } {

        const currentCabinet = { ...this.cabinet, ...changes };
        const overlapInfos = this.doorPositions.map(dp => new OverlapInfo(dp,
            currentCabinet.width!, //TODO account for outer gables
            this.doorOverlapWidth
        ));
        const doorProduct = this.getDoorProduct(changes);
        const possibleOverlapInfos = overlapInfos.filter(oi => doorProduct?.doorSizes?.some(ds =>
            ds.min.x <= oi.doorWidth
            && ds.max.x >= oi.doorWidth
            && ds.min.y <= currentCabinet.height!
            && ds.max.y >= currentCabinet.height!));
        const doorCounts = possibleOverlapInfos?.map(oi => oi.doorPositions.length);
        return {
            min: Math.min(...doorCounts),
            max: Math.max(...doorCounts)
        };
            
    }
    private readonly doorPositions = [
        //[],             // 0 doors  //invalid
        //[0],            // 1 door   //invalid
        [0, 1],           // 2
        [1, 0, 1],        // 3
        [1, 0, 1, 0],     // 4
        [1, 0, 0, 1],
        [1, 0, 1, 0, 1],  // 5
        [1, 0, 1, 1, 0],
        [1, 0, 1, 0, 1, 0],    // 6
        [1, 0, 1, 1, 0, 1],
        [1, 0, 1, 0 ,1, 0 ,1], // 7
        [1, 0, 0, 1, 0, 0, 1]
    ];

    getOverlapInfos(changes: Partial<ICabinet> = {}): OverlapInfo[] {
        const currentCabinet = { ...this.cabinet, ...changes };

        if (currentCabinet.doors.length === 0) {
            return [];
        }

        const possibleDoorPositions = this.doorPositions
            .filter(dp => dp.length === currentCabinet.doors.length);
        if (possibleDoorPositions.length < 0)
            throw new Error(`Overlap info for ${currentCabinet.doors.length} doors not implemented`);
        return possibleDoorPositions.map(dp => new OverlapInfo(dp,
            currentCabinet.width!, //TODO account for outer gables
            this.doorOverlapWidth
        ));
    }

    currentOverlapInfo(changes: Partial<ICabinet> = {}): OverlapInfo | undefined {
        const cabinet: ICabinet = {
            ...this.cabinet,
            ...changes
        };
        const infos = this.getOverlapInfos(changes);
        const result = infos
            .find(oi => oi.numOverlaps === cabinet.numDoorOverlaps)
        return result;
    }


    getGableInfo(changes: Partial<ICabinet> = {}): GableInfo {
        const cabinet = {
            ...this.cabinet,
            ...changes,
        };
        if (!hasDimensions(cabinet))
            return {
                left: {
                    position: { x: 0, y: 0, z: 0 },
                    size: { x: 0, y: 0, z: 0 },
                    type: GableType.None
                },
                right: {
                    position: { x: 0, y: 0, z: 0 },
                    size: { x: 0, y: 0, z: 0 },
                    type: GableType.None
                }
            };
        return {
            left: this.getLeftSideInfo(cabinet),
            right: this.getRightSideInfo(cabinet),
        };
    }

    private getLeftSideInfo(cabinet: ICabinetWithDimensions): GableSideInfo {
        if (this.hasWall(cabinet, GableSide.Left)) {
            if (cabinet.sidePanel) {
                const pl = this.getProductLine(cabinet);
                const adapterSize = pl?.adapterProducts[0]?.size ?? { x: 0, y: 0, z: 0 };
                return {
                    type: GableType.SidePanel,
                    position: {
                        x: 0,
                        y: 0,// TODO account for floor
                        z: cabinet.depth - adapterSize.z
                    },
                    size: {
                        ...adapterSize,//TODO account for floor and roof
                        y: cabinet.height,
                    }
                }
            }
            else {
                return {
                    type: GableType.None,
                    position: { x: 0, y: 0, z: 0 },
                    size: { x: 0, y: 0, z: 0 },
                };
            }
        }
        else {
            const pl = this.getProductLine(cabinet);
            const gableSize = pl?.gableProducts[0]?.size ?? { x: 0, y: 0, z: 0 };

            return {
                type: GableType.Gable,
                position: { x: 0, y: 0, z: 0 },// TODO account for floor
                size: {
                    x: gableSize.x,
                    y: cabinet.height,//TODO account for floor and roof
                    z: cabinet.depth
                }
            };
        }
    }
    private getRightSideInfo(cabinet: ICabinetWithDimensions): GableSideInfo {
        if (this.hasWall(cabinet, GableSide.Right)) {
            if (cabinet.sidePanel) {
                const pl = this.getProductLine(cabinet);
                const adapterSize = pl?.adapterProducts[0]?.size ?? { x: 0, y: 0, z: 0 };
                return {
                    type: GableType.SidePanel,
                    position: {
                        x: cabinet.width - adapterSize.x,
                        y: 0,// TODO account for floor
                        z: cabinet.depth - adapterSize.z
                    },
                    size: {
                        ...adapterSize,
                        //TODO account for floor and roof
                        y: cabinet.height,
                    }
                }

            }
            else {
                return {
                    type: GableType.None,
                    position: { x: cabinet.width, y: 0, z: 0 },
                    size: { x: 0, y: cabinet.height, z: cabinet.depth },
                };
            }
        }
        else {

            const pl = this.getProductLine(cabinet);
            const gableSize = pl?.gableProducts[0]?.size ?? { x: 0, y: 0, z: 0 };
            return {
                type: GableType.Gable,
                position: {
                    x: cabinet.width - gableSize.x,
                    y: 0,// TODO account for floor
                    z: 0,
                },
                size: {
                    x: gableSize.x,
                    y: cabinet.height,//TODO account for floor and roof
                    z: cabinet.depth
                }
            };
        }
    }

    hasWall(cabinet: ICabinet, side: GableSide) {
        const wall = side === GableSide.Left
            ? CabinetPosition.WallLeft
            : CabinetPosition.WallRight;
        return cabinet.position === CabinetPosition.WallBoth || cabinet.position === wall;
    }

    getGableMaterials(gableSide: GableSide, changes: Partial<ICabinet> = {}): IMaterial[] {
        const cabinet = {
            ...this.cabinet,
            ...changes
        };
        const productLine = this.getProductLine(changes);
        const gableProducts = productLine?.gableProducts.filter(gp => gp.size.z === cabinet.depth) ?? [];
        const materials = this.hasWall(cabinet, gableSide)
            ? productLine?.adapterMaterials ?? []
            : gableProducts.flatMap(gp => gp.materials)
            ;
        return materials;
    }

    setGableMaterialId(id: number, side: GableSide): Promise<ChangeResult> {
        const changes: Partial<ICabinet> = {};
        if (side === GableSide.Left) {
            changes.leftGableMaterialId = id;
        }
        else {
            changes.rightGableMaterialId = id;
        }
        this.change(changes);
        return Promise.resolve({ success: true });
    }

    private getFillingMaterial(id: number, changes: Partial<ICabinet>) {
        return this.getDoorProduct(changes)?.doorSizes.find(ds => ds.material.id === id)?.material;
    }

    private getBandFillingMaterial(id: number | null, changes: Partial<ICabinet>): IMaterial | undefined {
        return this.getDoorProduct(changes)?.bandMaterials.find(bm => bm.id === id);
    }

    getDoorBar(barId: number, changes: Partial<ICabinet>) {
        return this.getDoorProduct(changes)?.bars.find(b => b.id === barId);
    }

    getProductLine(changes: Partial<ICabinet> = {}): IProductLine | undefined {
        const cabinet = {
            ...this.cabinet,
            ...changes
        };
        const productLine = this.chainInfo.productLines.find(pl => pl.id === cabinet.productLineId);
        return productLine;
    }

    getDoorProduct(changes: Partial<ICabinet> = {}): IDoorProduct | undefined {
        const cabinet = {
            ...this.cabinet,
            ...changes
        };
        const door = this.getProductLine(changes)?.doors.find(dp => dp.id === cabinet.doorProductId);
        return door;
    }

    getSightRect(changes: Partial<ICabinet> = {}): IObj2d | undefined {
        const cabinet = { ...this.cabinet, ...changes };
        if (!cabinet.width)
            return undefined;
        if (!cabinet.height)
            return undefined;

        const gableInfo = this.getGableInfo(changes);
        const position = {
            x: maxPos(gableInfo.left).x,
            y: 0 // TODO account for floor
        }
        const result = {
            position,
            size: {
                x: Math.abs(gableInfo.right.position.x - position.x),
                y: cabinet.height //TODO account for floor and ceiling
            }
        };
        for (let dim of ['x', 'y'] as (keyof IVec2d)[])
            console.assert(result.size[dim] >= 0, `SightRect must be non-negative`, {
                gableInfo,
                position,
                result
            });
        return result;
    }
    private getDoorBarProduct(doorIndex: number, changes: Partial<ICabinet> = {}): IDoorBar | undefined {
        const cabinet = {
            ...this.cabinet,
            ...changes
        };
        const doorBarId = cabinet.doors[doorIndex]?.barId;
        return this.getDoorProduct(changes)?.bars.find(bar => bar.id === doorBarId);
    }

    async setProductLine(id: number): Promise<ChangeResult> {
        const newProductLine = this.chainInfo.productLines.find(pl => pl.id === id);
        if (!newProductLine)
            return failure();

        const resetTopics: ResetTopic[] = [];

        let resetDimensions = false;
        if (this.exceedsBounds(this.cabinet.width, newProductLine.minSize.x, newProductLine.maxSize.x))
            resetDimensions = true;
        if (this.exceedsBounds(this.cabinet.height, newProductLine.minSize.y, newProductLine.maxSize.y))
            resetDimensions = true;
        if (this.cabinet.depth && !newProductLine.gableProducts.find(gp => gp.size.z === this.cabinet.depth))
            resetDimensions = true;
        if (resetDimensions)
            resetTopics.push(ResetTopic.Dimensions);

        let resetDoors = false;
        if (this.cabinet.doorProductId !== null) {
            if (!newProductLine.doors.find(door => door.id === this.cabinet.doorProductId)) {
                resetDoors = true;
            }
        }
        if (resetDoors)
            resetTopics.push(ResetTopic.Doors);


        if (resetTopics.length > 0) {

            const shouldReset = await this.resetConfirmer(resetTopics);
            if (!shouldReset)
                return failure();
        }

        let changes: Partial<ICabinet> = { productLineId: id };
        if (resetDimensions) {
            changes.width = null;
            changes.height = null;
            changes.depth = null;
        }
        this.change(changes);
        return success();
    }


    async setCabinetPosition(position: CabinetPosition): Promise<ChangeResult> {
        const changes: Partial<ICabinet> = { position: position };
        if (position === CabinetPosition.FreeStanding) {
            changes.sidePanel = false;
        }
        this.change(changes);
        return success();
    }

    async setCabinetWidth(width: number): Promise<{ success: boolean, actualWidth: number }> {
        const productLine = this.chainInfo.productLines.find(pl => pl.id === this.cabinet.productLineId);
        if (!productLine)
            return { success: false, actualWidth: width };
        width = clamp(productLine.minSize.x, width, productLine.maxSize.x);

        //TODO check doors
        //TODO check interior

        this.change({ width: width });
        return { success: true, actualWidth: width };
    }

    async setCabinetHeight(height: number): Promise<ChangeResult & { actualHeight: number }> {
        const productLine = this.chainInfo.productLines.find(pl => pl.id === this.cabinet.productLineId);
        if (!productLine)
            return { success: false, actualHeight: height };
        height = clamp(productLine.minSize.y, height, productLine.maxSize.y);

        //TODO check doors
        //TODO check interior

        this.change({ height });
        return { success: true, actualHeight: height };
    }

    private updateDoorDimensions(changes: Partial<ICabinet> = {}): Door[] {
        const result: Door[] = [];
        const cabinet = {
            ...this.cabinet,
            ...changes
        };
        if (!cabinet.doors.length)
            return result;
        const doorInfos = this.getDoorInfos(changes);
        for (let i = 0; i < doorInfos.length; i++) {
            const door = cabinet.doors[i];
            const doorInfo = doorInfos[i];
            result.push(new Door({
                ...door,
                size: new Vec2d(doorInfo.size)
            }))
        }
        return result;
    }

    getErrorFlags(changes?: Partial<ICabinet>): ErrorFlag {
        let result = ErrorFlag.None;
        if (this.interior.getPulloutViolations().length > 0) {
            result |= ErrorFlag.Pullout;
        }
        if (this.interior.getWallMountViolations().length > 0) {
            result |= ErrorFlag.WallMount;
        }
        if (this.interior.getExceededBounds().length > 0) {
            result |= ErrorFlag.ExceedsBounds;
        }
        return result;
    }

    async setCabinetDepth(depth: number): Promise<ChangeResult> {
        //TODO validate
        this.change({ depth });
        return success();
    }

    async setRoof(roof: boolean): Promise<ChangeResult> {
        this.change({ roof: !!roof });
        return success();
    }

    async setBottom(bottom: boolean): Promise<ChangeResult> {
        this.change({ bottom: !!bottom });
        return success();
    }

    async setSidePanel(sidePanel: boolean): Promise<ChangeResult> {
        this.change({ sidePanel: !!sidePanel });
        return success();
    }


    async setDoorProduct(id: number|null): Promise<ChangeResult> {
        if (this.cabinet.doorProductId !== id) {

            if (this.cabinet.doors.length > 0) {
                const answer = await this.resetConfirmer([ResetTopic.Doors])
                if (!answer)
                    return failure();
            }

        }
        this.change({
            doorProductId: id,
            doors: [],
            doorDampening: undefined
        });
        return success();
    }

    async setNumDoors(numDoors: number): Promise<ChangeResult> {

        const numDoorChoices = this.getNumDoorChoices();
        numDoors = clamp(numDoorChoices.min, numDoors, numDoorChoices.max);

        if (numDoors === this.cabinet.doors.length)
            return success();

        const changes: Partial<ICabinet> = {};
        let doors = this.cabinet.doors;
        changes.doors = this.withNumDoors(numDoors, doors);

        //numOverlaps might not be valid
        const overlapInfos = this.getOverlapInfos(changes);
        if (!overlapInfos.find(oi => oi.numOverlaps === this.cabinet.numDoorOverlaps)) {
            changes.numDoorOverlaps = overlapInfos[0].numOverlaps;
        }

        this.change(changes);
        return success();
    }

    async setNumOverlaps(numOverlaps: number): Promise<ChangeResult> {

        this.change({ numDoorOverlaps: numOverlaps });
        return success();
    }

    canUseDampening(changes: Partial<ICabinet> = {}) {
        //TODO implement
        return true;
    }

    async setUseDamping(use: boolean): Promise<ChangeResult> {
        if (!this.canUseDampening())
            return failure();

        this.change({ doorDampening: use });
        return success();
    }

    async setDoorMaterial(doorIndex: number, materialId: number): Promise<ChangeResult> {
        this.changeDoor(doorIndex, { materialId: materialId });
        return success();
    }
    async setBandMaterial(doorIndex: number, id: number): Promise<ChangeResult> {
        this.changeDoor(doorIndex, { bandMaterialId: id });
        return success();
    }


    async setBar(doorIndex: number, barId: number): Promise<ChangeResult> {
        const bar = this.getDoorProduct()?.bars.find(b => b.id === barId);
        if (!bar)
            return failure();
        const door = this.cabinet.doors[doorIndex];
        if (!door)
            return failure();
        const changes: Partial<IDoor> = { barId: barId };

        if (door.bandMaterialId === null && bar.bandHeight !== null) {
            const bandMat = this.getBandFillingMaterial(door.bandMaterialId, {});
            if (!bandMat)
                return failure();
            changes.bandMaterialId = bandMat.id;
        }

        this.changeDoor(doorIndex, changes);
        return success();
    }

    getDoorInfos(changes: Partial<ICabinet> = {}): DoorInfo[] {
        const cabinet = {
            ...this.cabinet,
            ...changes
        };
        if (cabinet.doors.length === 0)
            return [];
        if (!cabinet.width)
            throw new Error("width not defined");
        if (!cabinet.height)
            throw new Error("height not defined");
        const doorProduct = this.getDoorProduct(changes);
        if (!doorProduct)
            throw new Error("Door product not defined");
        const sightRect = this.getSightRect(changes);
        if (!sightRect)
            throw new Error("Sight rect not defined");
        const overlapInfo = this.currentOverlapInfo(changes);
        if (!overlapInfo)
            throw new Error("Overlap info not defined");

        //TODO review Poomas
        const doorThickness = 20; //Pooma
        const barThickness = 10; //Pooma
        const railTrackSpacing = 40; //Pooma

        const totalOverlapWidth = (cabinet.numDoorOverlaps ?? 0) * this.doorOverlapWidth;
        const width = (sightRect.size.x + totalOverlapWidth) / cabinet.doors.length;
        const roundedWidth = Math.round(width);
        const doorSize: IVec3d = {
            x: roundedWidth,
            y: sightRect.size.y,
            z: doorThickness
        }; //TODO account for outer gables
        for (let dim of ['x', 'y', 'z'] as (keyof IVec3d)[])
            console.assert(doorSize[dim] >= 0, `DoorSize should be non-negative`, {
                sightRect,
                totalOverlapWidth,
                doors: cabinet.doors,
                doorThickness
            });

        let nextDoorPosX = sightRect.position.x;

        const result: DoorInfo[] = [];
        for (let doorIndex = 0; doorIndex < cabinet.doors.length; doorIndex++) {
            const door = cabinet.doors[doorIndex];
            const barProduct = this.getDoorBarProduct(doorIndex);
            const fillingMaterial = this.getFillingMaterial(door.materialId, changes);
            if (!fillingMaterial)
                throw new Error(`Filling material invalid for door ${doorIndex} materialId ${door.materialId}`);
            const fillingTexture = fillingMaterial?.textureImageUrl ?? "";
            const railNumber = overlapInfo.doorPositions[doorIndex];

            if (doorIndex > 0) {
                nextDoorPosX += doorSize.x;
                {
                    const overlapsPrevious = (doorIndex === 0)
                        ? false //first door - no overlap with previous
                        : overlapInfo.doorPositions[doorIndex] !== overlapInfo.doorPositions[doorIndex - 1]
                        ;

                    if (overlapsPrevious)
                        nextDoorPosX -= this.doorOverlapWidth;
                }
            }
            const position: IVec3d = {
                x: nextDoorPosX,
                y: 0,
                z: cabinet.depth! - (railNumber * railTrackSpacing) - doorThickness,
            };

            //Construct frame elements
            let frameElements: FrameElement[];
            {
                const frame = doorProduct.frame;
                const posZ = - (frame.thickness / 2);
                frameElements = [
                    //Top
                    {
                        frameName: "top",
                        size: {
                            x: doorSize.x,
                            y: frame.heightTop,
                            z: frame.thickness
                        },
                        position: {
                            x: 0,
                            y: doorSize.y - frame.heightTop,
                            z: posZ
                        },
                        textureUrl: frame.textureUrl
                    },
                    //Bottom
                    {
                        frameName: "bottom",
                        size: {
                            x: doorSize.x,
                            y: frame.heightBotton,
                            z: frame.thickness
                        },
                        position: {
                            x: 0,
                            y: 0,
                            z: posZ
                        },
                        textureUrl: frame.textureUrl
                    },
                    //Left
                    {
                        frameName: "left",
                        size: {
                            x: frame.widthSides,
                            y: doorSize.y,
                            z: frame.thickness
                        },
                        position: {
                            x: 0,
                            y: 0,
                            z: posZ
                        },
                        textureUrl: frame.textureUrl
                    },
                    //Right
                    {
                        frameName: "right",
                        size: {
                            x: frame.widthSides,
                            y: doorSize.y,
                            z: frame.thickness
                        },
                        position: {
                            x: doorSize.x - frame.widthSides,
                            y: 0,
                            z: posZ
                        },
                        textureUrl: frame.textureUrl
                    }
                ];
            }



            let barFrameElements: FrameElement[];
            let fillingElements: FillingElement[];
            if (!barProduct?.bandHeight) {
                //use normal bars

                fillingElements = [{
                    position: {
                        x: 0,
                        y: 0,
                        z: 0
                    },
                    size: doorSize,
                    textureUrl: fillingTexture,
                }
                ];


                barFrameElements = barProduct ? range(barProduct.numBars)
                    .map(barIndex => {
                        return {
                            frameName: `bar_${barIndex}` as `bar_${number}`,
                            textureUrl: barProduct.textureUrl,
                            position: {
                                x: 0,
                                y: doorSize.y * (barIndex + 1) / (barProduct.numBars + 1),
                                z: doorSize.z
                            },
                            size: {
                                x: doorSize.x,
                                y: barProduct.barHeight,
                                z: barThickness
                            }
                        };
                    })
                    : [];


            }
            else {
                //bars and bands
                if (barProduct.numBars !== 2)
                    throw new Error("Number of bars other than 2 with bands is not implemented");
                const bandFillingMaterial = this.getBandFillingMaterial(door.bandMaterialId, changes);
                if (!bandFillingMaterial)
                    throw new Error(`invalid band filling texture for door ${doorIndex} bandMaterialId ${door.bandMaterialId}`);
                const bandFillingTexture = bandFillingMaterial.textureImageUrl;
                const bandHeight = 450;
                const bandDepth = 10;
                const bandYStart = (doorSize.y / 2) - (bandHeight / 2);
                const bandPos = {
                    x: 0,
                    y: bandYStart,
                    z: 0
                };
                const bandSize = {
                    x: doorSize.x,
                    y: bandHeight,
                    z: bandDepth
                };
                barFrameElements = [
                    {
                        frameName: 'bar_0',
                        textureUrl: barProduct.textureUrl,
                        position: {
                            x: bandPos.x,
                            y: bandPos.y - (barProduct.barHeight / 2),
                            z: doorSize.z
                        },
                        size: {
                            x: doorSize.x,
                            y: barProduct.barHeight,
                            z: barThickness
                        }
                    },
                    {
                        frameName: 'bar_1',
                        textureUrl: barProduct.textureUrl,
                        position: {
                            x: bandPos.x,
                            y: bandPos.y + bandSize.y - (barProduct.barHeight / 2),
                            z: doorSize.z
                        },
                        size: {
                            x: doorSize.x,
                            y: barProduct.barHeight,
                            z: barThickness
                        }
                    }
                ];

                fillingElements = [
                    {
                        position: {
                            x: 0,
                            y: 0,
                            z: 0
                        },
                        size: {
                            x: doorSize.x,
                            y: bandPos.y,
                            z: doorSize.z
                        },
                        textureUrl: fillingTexture,
                    },
                    {
                        position: bandPos,
                        size: bandSize,
                        textureUrl: bandFillingTexture ?? "",
                    },
                    {
                        position: {
                            x: 0,
                            y: bandPos.y + bandSize.y,
                            z: 0
                        },
                        size: {
                            x: doorSize.x,
                            y: bandPos.y,
                            z: doorSize.z
                        },
                        textureUrl: fillingTexture
                    }
                ];
            }

            result.push({
                size: doorSize,
                position,
                railNumber,
                index: doorIndex,
                fillingElements,
                frameElements: barFrameElements.concat(frameElements)
            });
        }
        return result;
    }
}

